import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** Кружок — не длиннее минуты, как в приложении. */
const MAX_NOTE_MS = 60 * 1000;

/** Какой формат видео умеет записать браузер: mp4 — лучше для телефонов, иначе webm. */
const pickVideoMime = () => {
    if (typeof window === "undefined" || !window.MediaRecorder?.isTypeSupported) return "";
    const options = [
        "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
        "video/mp4",
        "video/webm;codecs=vp8,opus",
        "video/webm",
    ];
    return options.find((type) => window.MediaRecorder.isTypeSupported(type)) || "";
};

const formatTime = (ms) => {
    const seconds = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/*
 * Запись кружка с веб-камеры. Сначала видно себя в круге, «Записать» —
 * пошла запись (до минуты, по кругу бежит полоска). Потом запись можно
 * посмотреть и отправить сразу, отложить или переснять.
 */
export default function VideoNoteRecorder({ onClose, onSend, onSchedule }) {
    const liveRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const startedRef = useRef(0);
    const timerRef = useRef(null);

    const [phase, setPhase] = useState("starting"); // starting | ready | recording | review | error
    const [error, setError] = useState("");
    const [elapsed, setElapsed] = useState(0);
    const [result, setResult] = useState(null); // { blob, url, durationMs }

    const stopTimer = () => {
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = null;
    };

    const stopStream = useCallback(() => {
        streamRef.current?.getTracks?.().forEach((track) => track.stop());
        streamRef.current = null;
    }, []);

    const startCamera = useCallback(async () => {
        setPhase("starting");
        setError("");

        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setPhase("error");
            setError("Этот браузер не умеет записывать видео. Откройте чат в Chrome, Edge или Яндекс Браузере.");
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 640 }, aspectRatio: { ideal: 1 } },
                audio: true,
            });
            streamRef.current = stream;
            if (liveRef.current) {
                liveRef.current.srcObject = stream;
                liveRef.current.play().catch(() => undefined);
            }
            setPhase("ready");
        } catch (cameraError) {
            setPhase("error");
            setError(
                cameraError?.name === "NotAllowedError"
                    ? "Нет доступа к камере или микрофону. Разрешите их для сайта в настройках браузера."
                    : "Камера не найдена или занята другой программой."
            );
        }
    }, []);

    useEffect(() => {
        startCamera();
        return () => {
            stopTimer();
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                recorderRef.current.onstop = null;
                recorderRef.current.stop();
            }
            stopStream();
        };
    }, [startCamera, stopStream]);

    // Запись больше не нужна — освобождаем память.
    useEffect(() => () => {
        if (result?.url) URL.revokeObjectURL(result.url);
    }, [result]);

    const stopRecording = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== "inactive") recorder.stop();
    }, []);

    const startRecording = () => {
        const stream = streamRef.current;
        if (!stream) return;

        const mimeType = pickVideoMime();
        let recorder;
        try {
            recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        } catch (_error) {
            recorder = new MediaRecorder(stream);
        }

        chunksRef.current = [];
        recorder.ondataavailable = (event) => {
            if (event.data?.size) chunksRef.current.push(event.data);
        };
        recorder.onstop = () => {
            stopTimer();
            const durationMs = Math.min(MAX_NOTE_MS, Date.now() - startedRef.current);
            const type = String(recorder.mimeType || mimeType || "video/webm").split(";")[0];
            const blob = new Blob(chunksRef.current, { type });
            chunksRef.current = [];
            recorderRef.current = null;

            if (!blob.size || durationMs < 700) {
                setPhase("ready");
                setError("Запись слишком короткая — подержите запись хотя бы секунду.");
                return;
            }

            setResult({ blob, url: URL.createObjectURL(blob), durationMs });
            setPhase("review");
            // Камера больше не нужна: гасим лампочку, пока смотрят запись.
            stopStream();
        };

        recorderRef.current = recorder;
        startedRef.current = Date.now();
        setElapsed(0);
        setError("");
        recorder.start(250);
        setPhase("recording");

        timerRef.current = window.setInterval(() => {
            const spent = Date.now() - startedRef.current;
            setElapsed(spent);
            if (spent >= MAX_NOTE_MS) stopRecording();
        }, 200);
    };

    const retake = () => {
        setResult(null);
        startCamera();
    };

    const close = () => {
        if (phase === "recording") stopRecording();
        onClose?.();
    };

    const progress = Math.min(1, elapsed / MAX_NOTE_MS);
    const circle = 2 * Math.PI * 152;

    if (typeof document === "undefined") return null;

    return createPortal(
        <div
            className="chat-note-recorder-AO"
            role="dialog"
            aria-modal="true"
            aria-label="Записать кружок"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && phase !== "recording") close();
            }}
        >
            <div className="chat-note-recorder-AO__panel">
                <div className="chat-note-recorder-AO__head">
                    <b>Кружок</b>
                    <button type="button" onClick={close} aria-label="Закрыть">✕</button>
                </div>

                <div className={`chat-note-recorder-AO__circle is-${phase}`}>
                    {phase === "review" && result ? (
                        <video src={result.url} autoPlay loop playsInline controls={false} />
                    ) : (
                        <video ref={liveRef} muted playsInline autoPlay className="is-mirror" />
                    )}
                    <svg viewBox="0 0 320 320" aria-hidden="true">
                        <circle cx="160" cy="160" r="152" className="is-track" />
                        {phase === "recording" ? (
                            <circle
                                cx="160"
                                cy="160"
                                r="152"
                                className="is-progress"
                                strokeDasharray={circle}
                                strokeDashoffset={circle * (1 - progress)}
                            />
                        ) : null}
                    </svg>
                    {phase === "starting" ? <span className="chat-note-recorder-AO__status">Включаем камеру…</span> : null}
                </div>

                <div className="chat-note-recorder-AO__time">
                    {phase === "recording" ? <><i /> {formatTime(elapsed)} / 1:00</> : null}
                    {phase === "review" && result ? `Длительность ${formatTime(result.durationMs)}` : null}
                </div>

                {error ? <div className="chat-note-recorder-AO__error" role="alert">{error}</div> : null}

                <div className="chat-note-recorder-AO__actions">
                    {phase === "ready" ? (
                        <button type="button" className="is-primary is-record" onClick={startRecording}>● Записать</button>
                    ) : null}
                    {phase === "recording" ? (
                        <button type="button" className="is-primary" onClick={stopRecording}>■ Готово</button>
                    ) : null}
                    {phase === "review" && result ? (
                        <>
                            <button type="button" onClick={retake}>↺ Переснять</button>
                            {onSchedule ? (
                                <button type="button" onClick={() => onSchedule(result)}>◷ Отправить позже</button>
                            ) : null}
                            <button type="button" className="is-primary" onClick={() => onSend?.(result)}>➤ Отправить</button>
                        </>
                    ) : null}
                    {phase === "error" ? (
                        <button type="button" onClick={startCamera}>Попробовать ещё раз</button>
                    ) : null}
                </div>
            </div>
        </div>,
        document.body
    );
}
